import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatINR } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";

const STAGES = ["soft_collections", "field_visit", "promise_to_pay", "legal_notice", "repossession", "yard", "resale", "recovered", "written_off", "closed"];

export default function CollectionsDashboard() {
  const [data, setData] = useState({ loans: [], buckets: {} });
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [ptp, setPtp] = useState("");
  const [stage, setStage] = useState("");

  const load = () => api.get("/collections/portfolio").then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  const BUCKETS = ["Current", "1-30 DPD", "31-60 DPD", "61-90 DPD", "90+ DPD"];

  const markPaid = async (loan, idx) => {
    try {
      await api.post(`/applications/${loan.id}/payments/mark`, { emi_index: idx, status: "paid" });
      toast.success(`EMI #${idx} marked paid`);
      load();
      const { data } = await api.get(`/applications/${loan.id}`);
      setSelected(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setCollectionStage = async () => {
    if (!selected || !stage) return;
    try {
      await api.post(`/applications/${selected.id}/collections/stage`, { stage, note: note || null });
      toast.success(`Stage updated to ${stage.replace(/_/g, " ")}`);
      setStage(""); setNote("");
      const { data } = await api.get(`/applications/${selected.id}`);
      setSelected(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addNote = async () => {
    if (!selected || !note.trim()) return;
    try {
      await api.post(`/applications/${selected.id}/collections/note`, { note, promise_to_pay_date: ptp || null });
      toast.success("Note added");
      setNote(""); setPtp("");
      const { data } = await api.get(`/applications/${selected.id}`);
      setSelected(data);
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div data-testid="collections-dashboard">
      <h1 className="text-2xl font-semibold text-slate-900 mb-1">Collections Portfolio</h1>
      <p className="text-sm text-slate-600 mb-6">Active loans by DPD bucket.</p>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {BUCKETS.map(b => (
          <Card key={b} className="p-5 border-slate-200" data-testid={`coll-bucket-${b.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-xs uppercase tracking-wider text-slate-500">{b}</div>
            <div className="text-3xl font-semibold text-slate-900 mt-1 font-mono">{data.buckets?.[b] ?? 0}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card className="lg:col-span-3 border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 font-semibold text-slate-900 text-sm">Active loans</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr><th className="text-left px-4 py-3">App ID</th><th className="text-left px-4 py-3">Borrower</th><th className="text-left px-4 py-3">DPD</th><th className="text-left px-4 py-3">Bucket</th><th className="text-left px-4 py-3">Loan</th></tr>
            </thead>
            <tbody>
              {(data.loans || []).map(l => (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(l)} data-testid={`coll-row-${l.application_id}`}>
                  <td className="px-4 py-3 font-mono text-teal-700">{l.application_id}</td>
                  <td className="px-4 py-3">{l.borrower?.full_name}</td>
                  <td className="px-4 py-3 font-mono">{l.dpd}</td>
                  <td className="px-4 py-3"><StatusBadge status={l.bucket === "Current" ? "Active Loan" : l.bucket === "90+ DPD" ? "Rejected" : "Documents Pending"} /></td>
                  <td className="px-4 py-3 font-mono">{formatINR(l.loan_offer?.loan_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card className="lg:col-span-2 p-5 border-slate-200" data-testid="coll-detail">
          {!selected ? (
            <div className="text-slate-500 text-sm">Select a loan to add notes and mark EMIs.</div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">{selected.application_id}</div>
                <div className="font-semibold text-slate-900">{selected.borrower?.full_name}</div>
                <div className="text-sm text-slate-600">{selected.vehicle?.make} {selected.vehicle?.model}</div>
                {selected.collections_stage?.stage && (
                  <div className="mt-1.5 inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-orange-50 border border-orange-200 text-orange-800 capitalize" data-testid="coll-current-stage">
                    Stage: {selected.collections_stage.stage.replace(/_/g, " ")}
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Collections stage</div>
                <div className="flex gap-2">
                  <Select value={stage} onValueChange={setStage}>
                    <SelectTrigger className="h-9" data-testid="coll-stage-select"><SelectValue placeholder="Move to stage…" /></SelectTrigger>
                    <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="outline" disabled={!stage} onClick={setCollectionStage} data-testid="coll-stage-save-btn">Set</Button>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Next EMIs</div>
                <div className="space-y-2">
                  {(selected.emi_schedule || []).slice(0, 4).map((s, i) => (
                    <div key={i} className="flex items-center justify-between border border-slate-200 rounded-md p-2 text-sm">
                      <div>
                        <div className="font-mono">#{s.emi_no} · {s.due_date}</div>
                        <div className="text-xs text-slate-500">{formatINR(s.emi)} · status {s.status}</div>
                      </div>
                      {s.status !== "paid" && (
                        <Button size="sm" variant="outline" data-testid={`coll-mark-paid-${s.emi_no}`} onClick={() => markPaid(selected, s.emi_no)}>Mark paid</Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Add collection note</div>
                <Textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Call notes…" data-testid="coll-note-input" />
                <div className="mt-2 flex gap-2 items-center">
                  <Input type="date" value={ptp} onChange={e => setPtp(e.target.value)} placeholder="Promise to pay" data-testid="coll-ptp-input" />
                  <Button onClick={addNote} className="bg-slate-900 hover:bg-slate-800" data-testid="coll-add-note-btn">Save</Button>
                </div>
              </div>
              {selected.collection_notes && selected.collection_notes.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Recent notes</div>
                  <div className="space-y-2 max-h-48 overflow-auto">
                    {selected.collection_notes.slice().reverse().map((n, i) => (
                      <div key={i} className="text-sm border border-slate-100 rounded-md p-2">
                        <div className="text-xs text-slate-500">{n.at} · {n.by}</div>
                        <div>{n.note}</div>
                        {n.promise_to_pay_date && <div className="text-xs text-teal-700 mt-1">PTP: {n.promise_to_pay_date}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
